import React from "react";
import { CheckCircle, Loader, X } from "lucide-react";
import { User } from "types/user.type";

/**
 * KYC state as a badge.
 *
 * Shared so the segment pages and the full list read identically — the same
 * status meaning two different colours in two places is how an operator ends
 * up approving the wrong record.
 */
export const KycStatus = ({ item }: { item?: string }) => {
  switch (item) {
    case "Approved":
      return (
        <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-600 inline-flex items-center">
          <CheckCircle className="size-3 mr-1.5" />
          {item}
        </span>
      );
    case "In Review":
      return (
        <span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-slate-100 border-transparent text-slate-500">
          <Loader className="size-3 mr-1.5" />
          {item}
        </span>
      );
    case "Rejected":
      return (
        <span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-red-100 border-transparent text-red-500">
          <X className="size-3 mr-1.5" />
          {item}
        </span>
      );
    default:
      return (
        <span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-amber-50 border-transparent text-amber-600 inline-flex items-center">
          {item ?? "—"}
        </span>
      );
  }
};

interface Props {
  users?: User[];
  total?: number;
  isLoading?: boolean;
  emptyLabel?: string;
  onView: (user: User) => void;
  onArchive?: (event: React.MouseEvent, user: User) => void;
}

/**
 * The registered-users table.
 *
 * Extracted so the full list and each segment page render the same thing;
 * before, a segment was the same table with different state, and there was
 * nowhere to put a second copy without duplicating ninety lines of markup.
 *
 * `data-stack` and the per-cell `data-label`s drive the phone layout, where
 * each row becomes a labelled card (see `assets/scss/plugins/_stacked-table`).
 */
export const UsersTable: React.FC<Props> = ({
  users,
  total,
  isLoading,
  emptyLabel = "No users found",
  onView,
  onArchive,
}) => (
  <div className="overflow-x-auto">
    <table data-stack className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 border-b border-gray-200">
          {["Name", "Email", "Phone", "Joined", "Status"].map((heading) => (
            <th
              key={heading}
              className="px-3.5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide"
            >
              {heading}
            </th>
          ))}
          {onArchive && (
            <th className="px-3.5 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Actions
            </th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {isLoading ? (
          <tr>
            <td colSpan={6} className="py-10 text-center text-sm text-gray-400">
              Loading users…
            </td>
          </tr>
        ) : (total ?? 0) < 1 ? (
          <tr>
            <td colSpan={6} className="py-10 text-center text-sm text-gray-500">
              {emptyLabel}
            </td>
          </tr>
        ) : (
          users?.map((user) => (
            <tr
              key={user._id}
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => onView(user)}
            >
              <td
                data-label="Name"
                data-cell="title"
                className="px-3.5 py-2.5 border-b border-gray-100"
              >
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center size-10 font-medium rounded-full shrink-0 bg-slate-200 text-slate-800">
                    {user.firstName?.charAt(0)}
                    {user.lastName?.charAt(0)}
                  </div>
                  <div className="grow">
                    <p className="font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{user.role}</p>
                  </div>
                </div>
              </td>
              <td
                data-label="Email"
                className="px-3.5 py-2.5 border-b border-gray-100 break-all"
              >
                {user.email}
              </td>
              {/* The flex lives on a span, not the cell: `display: flex` on a
                  <td> takes it out of the table's column algorithm, so this
                  column never lined up with its header at any width. */}
              <td
                data-label="Phone"
                className="px-3.5 py-2.5 border-b border-gray-100"
              >
                <span className="flex items-center justify-end md:justify-start gap-2">
                  {user.phoneNumber}
                  {user.isPhoneVerified ? (
                    <CheckCircle className="size-3 text-green-500 shrink-0" />
                  ) : (
                    <Loader className="size-3 text-slate-400 shrink-0" />
                  )}
                </span>
              </td>
              <td
                data-label="Joined"
                className="px-3.5 py-2.5 border-b border-gray-100"
              >
                {new Date(user.createdAt).toLocaleDateString()}
              </td>
              <td
                data-label="Status"
                className="px-3.5 py-2.5 border-b border-gray-100"
              >
                <KycStatus item={user.kycStatus} />
              </td>
              {onArchive && (
                <td
                  data-label="Actions"
                  data-cell="actions"
                  className="px-3.5 py-2.5 border-b border-gray-100"
                >
                  <button
                    onClick={(event) => onArchive(event, user)}
                    className="px-3 py-2 bg-red-500 text-sm rounded-md text-white hover:bg-red-600 transition-colors"
                  >
                    Archive
                  </button>
                </td>
              )}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export default UsersTable;
